import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { AttentionProject } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { OVERALL_META, RAG_META, formatYmd } from "./report-status-meta";

/**
 * T-44b — "Needs attention": active projects whose latest submitted weekly report is at risk,
 * off track, or has a red schedule/scope/budget — with the PM's own words for WHY (demo L818:
 * "green/yellow/red and why"). Order comes from the API (off track → at risk → red).
 */
const TRIO: { key: "schedule" | "scope" | "budget_status"; label: string }[] = [
  { key: "schedule", label: "Schedule" },
  { key: "scope", label: "Scope" },
  { key: "budget_status", label: "Budget" },
];

export interface NeedsAttentionCardProps {
  projects: AttentionProject[] | undefined;
  isLoading?: boolean;
}

export function NeedsAttentionCard({
  projects,
  isLoading = false,
}: NeedsAttentionCardProps): JSX.Element {
  const list = projects ?? [];

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-text-primary">
          Needs Attention
          {!isLoading && (
            <Badge
              variant={list.length > 0 ? "danger" : "neutral"}
              className="ml-2"
            >
              {list.length}
            </Badge>
          )}
        </h3>
        <p className="text-sm text-text-secondary">
          Projects reported at risk, off track, or red on schedule, scope or
          budget
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3" data-testid="attention-loading">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-secondary">
            No projects need attention — every latest report is on track with no
            reds.
          </p>
        ) : (
          <ul
            className="divide-y divide-outlines"
            aria-label="Projects needing attention"
          >
            {list.map((p) => {
              const overall = OVERALL_META[p.overall_status];
              return (
                <li
                  key={p.jira_key}
                  className="space-y-2 py-4 first:pt-0 last:pb-0"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-col">
                      <span className="font-medium text-text-primary">
                        {p.title}
                      </span>
                      <span className="text-xs text-text-secondary">
                        <span className="font-mono">{p.jira_key}</span>
                        {" · "}PM: {p.project_manager || "—"}
                        {" · "}Week ending {formatYmd(p.week_ending)}
                      </span>
                    </div>
                    <Badge variant={overall.variant}>{overall.label}</Badge>
                  </div>

                  <ul
                    className="flex flex-wrap gap-2"
                    aria-label={`RAG for ${p.title}`}
                  >
                    {TRIO.map(({ key, label }) => {
                      const value = p[key];
                      const meta = value ? RAG_META[value] : null;
                      return (
                        <li key={key}>
                          <Badge
                            variant={meta?.variant ?? "neutral"}
                            className="text-xs"
                          >
                            {label}: {meta?.label ?? "Not set"}
                          </Badge>
                        </li>
                      );
                    })}
                  </ul>

                  <p className="text-sm text-text-primary">
                    <span className="font-medium">Why: </span>
                    {p.project_health ?? (
                      <span className="text-text-secondary">
                        No health summary provided.
                      </span>
                    )}
                  </p>
                  {p.top_risk && (
                    <p className="text-sm text-text-secondary">
                      <span className="font-medium text-text-primary">
                        Top risk:{" "}
                      </span>
                      {p.top_risk}
                    </p>
                  )}

                  <Button
                    asChild
                    variant="link"
                    size="sm"
                    className="h-auto gap-1 px-0"
                  >
                    <Link
                      to={`/projects/${encodeURIComponent(p.jira_key)}?tab=weekly-reports`}
                      aria-label={`Open weekly reports for ${p.title}`}
                    >
                      Open weekly reports
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
